import React, { useState } from 'react';
import { Table, Tag, Tooltip, Button, Checkbox, Radio, Input, Row, Col, Typography, Alert, Card, Select } from 'antd';

const { Option } = Select;
import { OUTPUT_COLUMNS, MASTER_CRITICAL_FIELDS, FIELD_DEFS } from '../../utils/headerAliases';
import { resolveValue } from '../../utils/referenceResolver';
import { buildAssetModelName } from '../../utils/assetModelName';
import { generateAssetSubtypeCode } from '../../utils/assetCodeGen';
import { recomputeRow } from '../../utils/smartImportPipeline';

const { Text } = Typography;

// Friendly explanations for each flag shown to the user.
export const FLAG_LABELS = {
  UNMATCHED_MASTER: ['orange', 'New to AssetPro', 'A type/sub-type/model/make/location here is not in AssetPro master data yet.'],
  NO_MASTER: ['red', 'Master data unavailable', 'Could not load AssetPro master data, so existing-vs-new could not be checked.'],
  AMBIGUOUS_DATE: ['volcano', 'Ambiguous date', 'Purchase date could be read as either day/month order — confirm it.'],
  INVALID_DATE: ['volcano', 'Bad date', 'Purchase date could not be parsed.'],
  DUPLICATE: ['magenta', 'Duplicate', 'Another row in this file has the same identifying details.'],
  QTY_ASSUMED: ['gold', 'QTY assumed 1', 'No quantity was supplied; assumed 1 unit. Confirm or set the real count.'],
  ZERO_QTY: ['gold', 'QTY is 0', 'Quantity is a literal zero — confirm that is intentional.'],
  AGGREGATE: ['purple', 'Type + count only', 'No per-unit identifiers; this is a quantity line, not an itemized asset.'],
  HEADER_AMBIGUOUS: ['default', 'Header uncertain', 'Header-row detection was not fully confident.'],
  SOURCE_COLUMNS_DROPPED: ['default', 'Extra columns', 'Some recognized columns were not part of the import template.'],
  MISSING_REQUIRED: ['red', 'Missing required', 'A required template field is empty — supply it before import.'],
  ROW_EMPTY: ['red', 'Empty row', 'No asset type / sub-type / model — nothing to import.'],
};

const statusMeta = {
  AUTO_ACCEPT: ['green', 'Auto-accept'],
  REVIEW: ['orange', 'Review'],
  BLOCKED: ['red', 'Blocked'],
};

const COL_WIDTH = 170;

/**
 * Step 3 UI: a spreadsheet-style editable grid. Every column of the final AssetPro import
 * CSV is shown inline with the mapped data; the user edits cells directly (auto-generated
 * code columns are read-only and recompute on export). Master-critical cells show a live
 * in-AssetPro / new badge. Include / Status / Issues are pinned. Props:
 *  - rows, masters, rowEdits, includes
 *  - onEdit(rowId, column, value), onToggleInclude(rowId, bool), onBulkInclude(statusFilter, bool)
 *  - onBack(), onExport()
 */
function ReviewQueue(props) {
  const {
    rows = [], masters, rowEdits = {}, includes = {},
    onEdit, onToggleInclude, onBulkFill, onBack, onExport,
  } = props;
  const [filter, setFilter] = useState('ALL');
  const [fillCol, setFillCol] = useState(undefined);
  const [fillMode, setFillMode] = useState('same');
  const [fillVal, setFillVal] = useState('');
  const [fillPrefix, setFillPrefix] = useState('');
  const [fillSuffix, setFillSuffix] = useState('');
  const [fillBlanks, setFillBlanks] = useState(true);

  const effective = (row, col) => {
    const e = rowEdits[row.rowId];
    if (e && e[col] !== undefined) return e[col];
    const v = row.output[col];
    return v === null || v === undefined ? '' : v;
  };

  const fieldKeyByColumn = {};
  Object.keys(FIELD_DEFS).forEach((fk) => { fieldKeyByColumn[FIELD_DEFS[fk].outputColumn] = fk; });

  // The value a cell currently shows: edits/source for most columns, but Model and (unmapped,
  // unedited) Asset Subtype Code are computed live so they track edits to Make / Sub Type.
  const cellValue = (r, col) => {
    const fk = fieldKeyByColumn[col];
    const edited = (rowEdits[r.rowId] || {})[col] !== undefined;
    if (fk === 'model') return buildAssetModelName(effective(r, 'Make'), effective(r, 'Asset Sub Type'), (r.extras || {}).modelNo);
    if (fk === 'assetSubtypeCode' && !(r.extras || {}).subtypeCodeMapped && !edited) return generateAssetSubtypeCode(effective(r, 'Asset Sub Type'));
    return effective(r, col);
  };

  // Live status/flags per row, recomputed from current cell values so bulk-fills and edits to
  // required fields (e.g. Purchase Date) clear "Missing required" and can flip a row to accepted.
  const liveByRow = {};
  rows.forEach((r) => { liveByRow[r.rowId] = recomputeRow(r, (col) => cellValue(r, col), masters); });
  const liveStatus = (r) => (liveByRow[r.rowId] || r).status;
  const statusCount = (s) => rows.filter((r) => liveStatus(r) === s).length;

  const filtered = rows.filter((r) => filter === 'ALL' || liveStatus(r) === filter);
  const includedCount = rows.filter((r) => includes[r.rowId]).length;

  const masterBadge = (fieldKey, value) => {
    const def = FIELD_DEFS[fieldKey];
    if (!def || !def.masterKind) return null;
    const res = resolveValue(def.masterKind, value, masters);
    if (res.status === 'EXACT') return <Tag color="green" style={{ marginTop: 2 }}>in AssetPro</Tag>;
    if (res.status === 'UNMATCHED') return <Tag color="orange" style={{ marginTop: 2 }}>new</Tag>;
    if (res.status === 'NO_MASTER') return <Tag style={{ marginTop: 2 }}>unchecked</Tag>;
    return null;
  };

  // One editable column per AssetPro template column.
  const editableColumns = OUTPUT_COLUMNS.map((col) => {
    const fk = fieldKeyByColumn[col];
    const def = fk ? FIELD_DEFS[fk] : null;
    const auto = def && def.autoGenerated;
    const required = def && def.required;
    const isMaster = fk && MASTER_CRITICAL_FIELDS.includes(fk);
    return {
      title: (
        <span>
          {col.trim()}{required && <span style={{ color: 'red' }}> *</span>}
          {auto && <span style={{ color: '#1890ff', fontSize: 11 }}> (auto)</span>}
        </span>
      ),
      key: col,
      width: COL_WIDTH,
      render: (_, r) => {
        const value = cellValue(r, col);
        return (
          <div>
            <Input
              size="small"
              value={value}
              disabled={auto}
              placeholder={auto ? '—' : ''}
              onChange={(e) => onEdit(r.rowId, col, e.target.value)}
            />
            {isMaster && masterBadge(fk, value)}
          </div>
        );
      },
    };
  });

  const columns = [
    {
      title: 'Inc.', key: 'include', width: 50, fixed: 'left',
      render: (_, r) => (
        <Checkbox checked={!!includes[r.rowId]} onChange={(e) => onToggleInclude(r.rowId, e.target.checked)} />
      ),
    },
    {
      title: 'Status', key: 'status', width: 100, fixed: 'left',
      render: (_, r) => { const s = liveStatus(r); const m = statusMeta[s] || ['default', s]; return <Tag color={m[0]}>{m[1]}</Tag>; },
    },
    ...editableColumns,
    {
      title: 'Issues', key: 'flags', width: 230, fixed: 'right',
      render: (_, r) => (
        <span>
          {((liveByRow[r.rowId] || r).flags || []).filter((f) => FLAG_LABELS[f]).map((f) => {
            const [color, label, tip] = FLAG_LABELS[f];
            return <Tooltip key={f} title={tip}><Tag color={color} style={{ marginBottom: 2 }}>{label}</Tag></Tooltip>;
          })}
        </span>
      ),
    },
  ];

  const scrollX = 50 + 100 + 230 + editableColumns.length * COL_WIDTH;

  return (
    <div>
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message="Review before export — this is your import sheet"
        description="Every column below is exactly what goes into the AssetPro import file, pre-filled from your data. Edit any cell directly (greyed cells are auto-generated codes). Master fields show whether the value already exists in AssetPro or is new. Tick the rows to include, then export. Nothing is guessed."
      />

      <Card size="small" title="Bulk fill a column" style={{ marginBottom: 12 }}>
        <Row gutter={[8, 8]} type="flex" align="middle">
          <Col>
            <Select
              placeholder="Choose column"
              value={fillCol}
              onChange={setFillCol}
              style={{ width: 190 }}
              showSearch
              optionFilterProp="children"
              size="small"
            >
              {OUTPUT_COLUMNS.filter((c) => {
                const fk = fieldKeyByColumn[c];
                return !(fk && FIELD_DEFS[fk] && FIELD_DEFS[fk].autoGenerated);
              }).map((c) => <Option key={c} value={c}>{c.trim()}</Option>)}
            </Select>
          </Col>
          <Col>
            <Radio.Group value={fillMode} onChange={(e) => setFillMode(e.target.value)} size="small">
              <Radio.Button value="same">Same</Radio.Button>
              <Radio.Button value="incremental">Incremental</Radio.Button>
              <Radio.Button value="affix">Prefix/Suffix</Radio.Button>
            </Radio.Group>
          </Col>
          {fillMode === 'affix' ? (
            <Col>
              <Input size="small" style={{ width: 110 }} placeholder="Prefix" value={fillPrefix} onChange={(e) => setFillPrefix(e.target.value)} />
              <Input size="small" style={{ width: 110, marginLeft: 6 }} placeholder="Suffix" value={fillSuffix} onChange={(e) => setFillSuffix(e.target.value)} />
            </Col>
          ) : (
            <Col>
              <Input
                size="small"
                style={{ width: 210 }}
                placeholder={fillMode === 'same' ? 'Value for every row (e.g. 0)' : 'Start value (e.g. INV-001)'}
                value={fillVal}
                onChange={(e) => setFillVal(e.target.value)}
              />
            </Col>
          )}
          {fillMode !== 'affix' && (
            <Col>
              <Checkbox checked={fillBlanks} onChange={(e) => setFillBlanks(e.target.checked)}>Blank cells only</Checkbox>
            </Col>
          )}
          <Col>
            <Button
              type="primary"
              size="small"
              disabled={!fillCol}
              onClick={() => onBulkFill(fillCol, { mode: fillMode, value: fillVal, prefix: fillPrefix, suffix: fillSuffix, blanksOnly: fillBlanks })}
            >
              Fill
            </Button>
          </Col>
        </Row>
        <Text type="secondary" style={{ fontSize: 12 }}>
          Same = one value for all rows (e.g. Purchase Vendor, or Purchase Amount 0). Incremental = numbering that steps up per row (e.g. invoice numbers). Prefix/Suffix = wrap each existing value (e.g. add "INV-" before invoice numbers).
        </Text>
      </Card>

      <Row style={{ marginBottom: 12 }} type="flex" justify="space-between" align="middle">
        <Col>
          <Radio.Group value={filter} onChange={(e) => setFilter(e.target.value)} size="small">
            <Radio.Button value="ALL">All ({rows.length})</Radio.Button>
            <Radio.Button value="AUTO_ACCEPT">Auto-accept ({statusCount('AUTO_ACCEPT')})</Radio.Button>
            <Radio.Button value="REVIEW">Review ({statusCount('REVIEW')})</Radio.Button>
            <Radio.Button value="BLOCKED">Blocked ({statusCount('BLOCKED')})</Radio.Button>
          </Radio.Group>
        </Col>
        <Col>
          <Button size="small" onClick={() => filtered.forEach((r) => onToggleInclude(r.rowId, true))} style={{ marginRight: 8 }}>Include shown</Button>
          <Button size="small" onClick={() => filtered.forEach((r) => onToggleInclude(r.rowId, false))}>Exclude shown</Button>
        </Col>
      </Row>

      <Table
        dataSource={filtered.map((r) => ({ key: r.rowId, ...r }))}
        rowKey="key"
        size="small"
        bordered
        pagination={{ pageSize: 20, size: 'small' }}
        columns={columns}
        scroll={{ x: scrollX }}
      />

      <Row gutter={16} style={{ marginTop: 16 }} type="flex" align="middle">
        <Col><Button onClick={onBack}>Back</Button></Col>
        <Col>
          <Button type="primary" onClick={onExport} disabled={includedCount === 0}>
            Export {includedCount} selected row(s)
          </Button>
        </Col>
        <Col><Text type="secondary">{includedCount} of {rows.length} selected for export</Text></Col>
      </Row>
    </div>
  );
}

export default ReviewQueue;
